<template>
  <modal-wrapper
    ref="modalRef"
    :busy="isValidatingSubmit"
    :cancel-button-label="t('generic.cancel')"
    :close-on-ok-click="false"
    :content-overflow-y="'visible'"
    :header="t('filesOverview.newUpload')"
    :ok-button-label="t('generic.select')"
    :size="ModalSize.L"
    @ok-button-click="handleModalOK"
    @show="onModalShow"
  >
    <div id="modal-container">
      <div class="input-container">
        <label for="reference-number-input">{{
          t('filesOverview.uploadId')
        }}</label>
        <div class="select-container">
          <PVInputText
            id="reference-number-input"
            v-model="referenceNumber"
            type="text"
          />
          <p
            v-if="!errorMessages.referenceNumber && isSubmitted"
            class="error-message"
          >
            {{ t('filesOverview.provideUploadId') }}
          </p>
          <p
            v-else-if="!errorMessages.referenceNumberUnique && isSubmitted"
            class="error-message"
          >
            {{ t('documentUpload.referenceNumberError') }}
          </p>
        </div>
      </div>

      <div class="input-container">
        <label>{{ t('filesOverview.selectContentType') }}</label>
        <div class="select-container">
          <styled-multiselect
            v-model="selectedContentType"
            class="multiselect"
            :options="allContentTypes"
            :placeholder="t('projectDetails.contentTypes.selectContentType')"
            :loading="isLoadingContentTypes"
            label="contentTypeName"
            track-by="contentTypeName"
          ></styled-multiselect>
          <p
            v-if="!errorMessages.contentType && isSubmitted"
            class="error-message"
          >
            {{ t('filesOverview.provideContentType') }}
          </p>
          <p v-if="isNoMappingAssignedError" class="error-message">
            {{ t('newDigitalizationUploadModal.noMappingAssigned') }}
          </p>
        </div>
      </div>

      <div class="input-container">
        <label>{{ $tt('filesOverview.checkBotConfiguration') }}</label>
        <div class="select-container">
          <check-bot-config-select
            v-model="selectedCheckBotConfig"
            :disabled="isCheckBotInspectionDisabled"
            :project-id="digitalizationProjectId"
          />
          <p v-if="isCheckBotConfigError" class="error-message">
            {{ $tt('filesOverview.provideCheckBotConfig') }}
          </p>
        </div>
      </div>

      <div class="input-container">
        <div class="upload-options-checkboxes">
          <div class="check-box-container">
            <PVCheckbox
              v-model="isCheckBotInspectionDisabled"
              binary
              class="upload-option-checkbox"
              input-id="check-bot-inspection-checkbox"
            />
            <label for="check-bot-inspection-checkbox">
              {{ t('newDigitalizationUploadModal.disableCheckBotInspection') }}
            </label>
          </div>
          <div class="check-box-container">
            <PVCheckbox
              v-model="isNoMetadataUploadMode"
              binary
              class="upload-option-checkbox"
              input-id="no-metadata-upload-checkbox"
            />
            <label for="no-metadata-upload-checkbox">
              {{ t('newDigitalizationUploadModal.noMetadataUpload') }}
            </label>
          </div>
        </div>
      </div>
    </div>
  </modal-wrapper>
</template>

<script setup lang="ts">
  import { ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useI18n } from 'vue-i18n';
  import { useToast } from '@/composables/useToast';
  import {
    ICheckBotConfigSummaryDto,
    IDigitalizationProjectContentTypesAssignmentsSummaryDto,
    ProjectType,
  } from 'types';
  import axios, { AxiosResponse } from 'axios';
  import endpointsList from '../../helpers/endpointsList';
  import { ModalSize } from '../ModalWrapper/ModalWrapper.types';
  import { PROJECT_TYPE_PARAM, Route, routeParams } from '@/routes';
  import { useModalWrapper } from '../ModalWrapper';
  import ModalWrapper from '../ModalWrapper/ModalWrapper.vue';
  import { computed } from 'vue';

  const props = defineProps<{
    digitalizationProjectId: string;
    digitalizationProjectNumber: string;
    digitalizationProjectTitle: string;
  }>();

  const allContentTypes = ref<
    IDigitalizationProjectContentTypesAssignmentsSummaryDto[]
  >([]);
  const errorMessages = ref({
    referenceNumber: false,
    referenceNumberUnique: false,
    contentType: false,
    checkBotConfig: false,
  });
  const isCheckBotInspectionDisabled = ref(false);
  const isLoadingContentTypes = ref(false);
  const isNoMappingAssignedError = ref(false);
  const isNoMetadataUploadMode = ref(false);
  const isSubmitted = ref(false);
  const isValidated = ref(false);
  const isValidatingSubmit = ref(false);
  const referenceNumber = ref('');
  const selectedContentType = ref<
    IDigitalizationProjectContentTypesAssignmentsSummaryDto | undefined
  >(undefined);
  const selectedCheckBotConfig = ref<ICheckBotConfigSummaryDto | null>(null);

  const { t } = useI18n();
  const { createErrorToast } = useToast();
  const router = useRouter();

  const { modalRef, show, hide } = useModalWrapper();
  defineExpose({ show, hide });

  const isCheckBotConfigError = computed(
    () =>
      isSubmitted.value &&
      !isCheckBotInspectionDisabled.value &&
      selectedCheckBotConfig.value === null,
  );

  async function getContentTypesForProject() {
    isLoadingContentTypes.value = true;
    try {
      const response = await axios.get(
        endpointsList.contentTypeMappings.getDigitalizationProjectContentTypesSummary(
          props.digitalizationProjectId,
        ),
      );
      allContentTypes.value = response.data;
    } catch {
      createErrorToast(t('errors.error'));
    }
    isLoadingContentTypes.value = false;
  }

  async function handleModalOK() {
    await validateSubmit();
    isSubmitted.value = true;
    if (!isValidated.value || !selectedContentType.value) {
      return;
    }
    router.push({
      name: Route.DOCUMENT_UPLOAD,
      query: {
        [routeParams[Route.DOCUMENT_UPLOAD].query[PROJECT_TYPE_PARAM]]:
          ProjectType.DIGITALIZATION_PROJECT,
        [routeParams[Route.DOCUMENT_UPLOAD].query.referenceNumber]:
          referenceNumber.value,
        [routeParams[Route.DOCUMENT_UPLOAD].query.contentTypeId]:
          selectedContentType.value._id,
        [routeParams[Route.DOCUMENT_UPLOAD].query.projectId]:
          props.digitalizationProjectId,
        [routeParams[Route.DOCUMENT_UPLOAD].query.projectName]:
          props.digitalizationProjectTitle,
        [routeParams[Route.DOCUMENT_UPLOAD].query.isCheckBotInspectionDisabled]:
          isCheckBotInspectionDisabled.value.toString(),
        [routeParams[Route.DOCUMENT_UPLOAD].query.isNoMetadataUploadMode]:
          isNoMetadataUploadMode.value.toString(),
        [routeParams[Route.DOCUMENT_UPLOAD].query.checkBotConfigId]:
          selectedCheckBotConfig.value?._id,
      },
    });
  }

  async function validateSubmit() {
    isValidatingSubmit.value = true;
    errorMessages.value.referenceNumber = !!referenceNumber.value;
    errorMessages.value.contentType = !!selectedContentType.value;
    errorMessages.value.checkBotConfig =
      isCheckBotInspectionDisabled.value || !!selectedCheckBotConfig.value;
    if (referenceNumber.value) {
      await checkIsReferenceNumberUnique();
    }

    if (Object.values(errorMessages.value).some((value) => !value)) {
      isValidated.value = false;
      isValidatingSubmit.value = false;
      return;
    }

    /**
     * If the upload is in no-metadata mode, the content type mapping is not
     * necessary.
     */
    if (
      !isNoMetadataUploadMode.value &&
      !selectedContentType.value?.isMapping
    ) {
      isNoMappingAssignedError.value = true;
      isValidated.value = false;
      isValidatingSubmit.value = false;
      return;
    }

    isValidated.value = true;
    isValidatingSubmit.value = false;
  }

  async function checkIsReferenceNumberUnique() {
    try {
      if (!referenceNumber.value) {
        errorMessages.value.referenceNumberUnique = false;
        return;
      }
      const response: AxiosResponse<boolean> = await axios.post(
        endpointsList.digitalizationUploads.validateReferenceNumber(
          props.digitalizationProjectId,
          encodeURIComponent(referenceNumber.value),
        ),
      );
      errorMessages.value.referenceNumberUnique = response.data;
    } catch {
      createErrorToast(t('documentUpload.errors.validationError'));
    }
  }

  function onModalShow() {
    getContentTypesForProject();
  }
</script>

<style lang="scss" scoped>
  #modal-container {
    display: flex;
    flex-direction: column;
    row-gap: 30px;

    .error-message {
      color: red;
      font-size: 16px;
      line-height: 20px;
      margin: 0;
      text-align: center;
      margin-top: 20px;
    }

    .input-container {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: center;

      label {
        width: 20%;
      }

      .select-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;

        .multiselect,
        #reference-number-input {
          min-width: 0;
          width: 90%;
        }
      }

      .upload-options-checkboxes {
        display: flex;
        flex-direction: column;
        row-gap: 0.3rem;

        .check-box-container {
          display: flex;
          column-gap: 0.5rem;

          label {
            width: auto;
          }
        }
      }
    }
  }
</style>
